import Config from '../../constants/appConfig';
import tools from './tools';

const STORAGE_KEY = 'company-units';

class Units {
    getStored = () => {
        try {
            const value = localStorage.getItem(STORAGE_KEY);
            return value ? JSON.parse(value) : {};
        } catch (error) {
            return {};
        }
    };

    setStored = (units) => {
        const value = units || {};
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            speed_unit: value.speed_unit || '',
            volume_unit: value.volume_unit || '',
            length_unit: value.length_unit || '',
            area_unit: value.area_unit || ''
        }));
    };

    clear = () => {
        localStorage.removeItem(STORAGE_KEY);
    };

    load = async () => {
        const token = localStorage.getItem('access-token-jwt');
        if (!token) {
            return this.getStored();
        }

        const options = {
            method: 'GET',
            headers: {
                'Authorization': token
            }
        };

        try {
            const configResponse = await fetch(Config.server + '/company_config', options);
            if (configResponse.status !== 200) {
                return this.getStored();
            }

            const config = await configResponse.json();
            const idCompany = config && config.length > 0 ? config[0].id_company : null;

            if (!idCompany) {
                return this.getStored();
            }

            const companyResponse = await fetch(Config.server + '/company/' + idCompany, options);
            if (companyResponse.status !== 200) {
                return this.getStored();
            }

            const company = await companyResponse.json();
            if (company && company.length > 0) {
                this.setStored(company[0]);
            }

            return this.getStored();
        } catch (error) {
            return this.getStored();
        }
    };

    isEnglish = (res) => {
        return res && res.LINGUA === 'en-US';
    };

    getSpeedUnit = (res) => {
        const units = this.getStored();
        return units.speed_unit || (this.isEnglish(res) ? 'mph' : 'km/h');
    };

    getVolumeUnit = (res, sourceUnit = null) => {
        const units = this.getStored();
        const configuredUnit = units.volume_unit || (this.isEnglish(res) ? 'gal(US)' : 'L');

        if (sourceUnit) {
            const sourceIsMass = sourceUnit === 'kg';
            const targetIsMass = configuredUnit === 'kg';
            if (sourceIsMass !== targetIsMass) {
                return sourceUnit;
            }
        }

        return configuredUnit;
    };

    getLengthUnit = (res) => {
        const units = this.getStored();
        return units.length_unit || (this.isEnglish(res) ? 'ft' : 'm');
    };

    getAreaUnit = (res) => {
        const units = this.getStored();
        return units.area_unit || (this.isEnglish(res) ? 'ac' : 'ha');
    };

    getFlowUnit = (res, sourceVolumeUnit = 'L') => {
        return `${this.getVolumeUnit(res, sourceVolumeUnit)}/${this.getAreaUnit(res)}`;
    };

    getAreaRateUnit = (res, timeUnit = 'h') => {
        return `${this.getAreaUnit(res)}/${timeUnit}`;
    };

    formatValue = (value, res, decimals = 1) => {
        const number = Number(value);
        const safeValue = Number.isFinite(number) ? number : 0;
        const locale = res && res.LINGUA ? res.LINGUA : 'pt-BR';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: false
        }).format(safeValue);
    };

    roundValue = (value, decimals = 1) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        return Number(number.toFixed(decimals));
    };

    convertSpeedTo = (value, sourceUnit = 'km/h', targetUnit = 'km/h') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        if (sourceUnit === targetUnit) return number;

        let kmh = number;
        if (sourceUnit === 'm/s') kmh = tools.mps_to_kmh(number);
        else if (sourceUnit === 'mph') kmh = tools.mph_to_kmh(number);
        else if (sourceUnit === 'kt') kmh = tools.kt_to_kmh(number);

        if (targetUnit === 'm/s') return tools.kmh_to_mps(kmh);
        if (targetUnit === 'mph') return tools.kmh_to_mph(kmh);
        if (targetUnit === 'kt') return tools.kmh_to_kt(kmh);
        return kmh;
    };

    convertSpeed = (value, sourceUnit = 'km/h', res) => {
        return this.convertSpeedTo(value, sourceUnit, this.getSpeedUnit(res));
    };

    toCanonicalSpeed = (value, res, canonicalUnit = 'km/h') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;

        const sourceUnit = this.getSpeedUnit(res);
        let kmh = number;
        if (sourceUnit === 'm/s') kmh = tools.mps_to_kmh(number);
        else if (sourceUnit === 'mph') kmh = tools.mph_to_kmh(number);
        else if (sourceUnit === 'kt') kmh = tools.kt_to_kmh(number);

        if (canonicalUnit === 'm/s') return tools.kmh_to_mps(kmh);
        return kmh;
    };

    convertLengthTo = (value, sourceUnit = 'm', targetUnit = 'm') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        if (sourceUnit === targetUnit) return number;

        let meters = number;
        if (sourceUnit === 'ft') meters = tools.ft_to_m(number);

        return targetUnit === 'ft' ? tools.m_to_ft(meters) : meters;
    };

    convertLength = (value, sourceUnit = 'm', res) => {
        return this.convertLengthTo(value, sourceUnit, this.getLengthUnit(res));
    };

    toCanonicalLength = (value, res) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        return this.getLengthUnit(res) === 'ft' ? tools.ft_to_m(number) : number;
    };

    convertAreaTo = (value, sourceUnit = 'ha', targetUnit = 'ha') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        if (sourceUnit === targetUnit) return number;

        let hectares = number;
        if (sourceUnit === 'ac') hectares = tools.ac_to_ha(number);
        else if (sourceUnit === 'm2') hectares = tools.m2_to_ha(number);

        if (targetUnit === 'ac') return tools.ha_to_ac(hectares);
        if (targetUnit === 'm2') return hectares * 10000;
        return hectares;
    };

    convertArea = (value, sourceUnit = 'ha', res) => {
        return this.convertAreaTo(value, sourceUnit, this.getAreaUnit(res));
    };

    toCanonicalArea = (value, res) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        return this.convertAreaTo(number, this.getAreaUnit(res), 'ha');
    };

    convertVolumeTo = (value, sourceUnit = 'L', targetUnit = 'L') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        if (sourceUnit === targetUnit) return number;
        if (sourceUnit === 'kg' || targetUnit === 'kg') return number;

        let liters = number;
        if (sourceUnit === 'gal(US)' || sourceUnit === 'gal') liters = tools.gal_to_l(number);
        else if (sourceUnit === 'gal(UK)') liters = tools.gal_uk_to_l(number);

        if (targetUnit === 'gal(US)' || targetUnit === 'gal') return tools.l_to_gal(liters);
        if (targetUnit === 'gal(UK)') return tools.l_to_gal_uk(liters);
        return liters;
    };

    convertVolume = (value, sourceUnit = 'L', res) => {
        return this.convertVolumeTo(value, sourceUnit, this.getVolumeUnit(res, sourceUnit));
    };

    toCanonicalVolume = (value, res) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;

        const sourceUnit = this.getVolumeUnit(res);
        if (sourceUnit === 'kg') return number;
        if (sourceUnit === 'gal(US)') return tools.gal_to_l(number);
        if (sourceUnit === 'gal(UK)') return tools.gal_uk_to_l(number);
        return number;
    };

    getUnitConfig = (res) => ({
        speed_unit: this.getSpeedUnit(res),
        volume_unit: this.getVolumeUnit(res),
        length_unit: this.getLengthUnit(res),
        area_unit: this.getAreaUnit(res)
    });

    getLegacyUnitConfig = (language) => ({
        speed_unit: language === 'en-US' ? 'mph' : 'km/h',
        volume_unit: language === 'en-US' ? 'gal(US)' : 'L',
        length_unit: language === 'en-US' ? 'ft' : 'm',
        area_unit: language === 'en-US' ? 'ac' : 'ha'
    });

    convertFlowTo = (value, sourceVolumeUnit = 'L', sourceAreaUnit = 'ha', targetVolumeUnit = 'L', targetAreaUnit = 'ha') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;

        const compatibleTargetVolume = (sourceVolumeUnit === 'kg') === (targetVolumeUnit === 'kg')
            ? targetVolumeUnit
            : sourceVolumeUnit;
        const volumeFactor = sourceVolumeUnit === 'kg'
            ? 1
            : this.convertVolumeTo(1, sourceVolumeUnit, compatibleTargetVolume);

        const areaFactor = this.convertAreaTo(1, sourceAreaUnit, targetAreaUnit);
        const safeAreaFactor = Number.isFinite(areaFactor) && areaFactor !== 0 ? areaFactor : 1;

        return number * volumeFactor / safeAreaFactor;
    };

    convertFlow = (value, sourceVolumeUnit = 'L', sourceAreaUnit = 'ha', res) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;

        const targetVolume = this.getVolumeUnit(res, sourceVolumeUnit);
        const targetArea = this.getAreaUnit(res);
        return this.convertFlowTo(number, sourceVolumeUnit, sourceAreaUnit, targetVolume, targetArea);
    };

    toCanonicalFlow = (value, res, targetVolumeUnit = 'L', targetAreaUnit = 'ha') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;

        const sourceVolume = this.getVolumeUnit(res, targetVolumeUnit);
        const sourceArea = this.getAreaUnit(res);

        return this.convertFlowTo(number, sourceVolume, sourceArea, targetVolumeUnit, targetAreaUnit);
    };

    convertAreaRate = (value, sourceAreaUnit = 'ha', res) => {
        const number = Number(value);
        if (!Number.isFinite(number)) return 0;
        return this.convertArea(number, sourceAreaUnit, res);
    };
}

const units = new Units();

export default units;
